import { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { FileCheck2, IndianRupee, ScrollText, ShieldCheck, ArrowRight } from 'lucide-react';
import { usePortalI18n } from '../../context/PortalI18nContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TONE = {
  draft: 'bg-slate-100 text-slate-700',
  submitted: 'bg-sky-100 text-sky-800',
  in_process: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  payment_initiated: 'bg-blue-100 text-blue-800',
  paid: 'bg-emerald-600 text-white',
};

const PortalDashboard = () => {
  const { division } = useOutletContext();
  const { t, tStatus, lang } = usePortalI18n();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, p, c] = await Promise.all([
          axios.get(`${API}/portal/dashboard`, { withCredentials: true }),
          axios.get(`${API}/portal/policies`, { withCredentials: true }),
          axios.get(`${API}/portal/claims`, { withCredentials: true }),
        ]);
        setStats(d.data);
        setPolicies(p.data || []);
        setClaims(c.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  const tiles = [
    { label: t('dash.tile.activeGrants'), value: stats?.active_policies ?? 0, icon: ScrollText, tone: 'text-emerald-700 bg-emerald-50', key: 'active-grants' },
    { label: t('dash.tile.totalClaimed'), value: `₹${(stats?.total_claimed || 0).toLocaleString('en-IN')}`, icon: IndianRupee, tone: 'text-amber-700 bg-amber-50', key: 'total-claimed' },
    { label: t('dash.tile.received'), value: `₹${(stats?.total_received || 0).toLocaleString('en-IN')}`, icon: IndianRupee, tone: 'text-blue-700 bg-blue-50', key: 'received' },
    { label: t('dash.tile.kycDocs'), value: stats?.kyc_count ?? 0, icon: ShieldCheck, tone: 'text-violet-700 bg-violet-50', key: 'kyc-docs' },
  ];

  const dateLocale = lang === 'hi' ? 'hi-IN' : 'en-IN';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">
            {t('dash.welcome', { name: division.secretary_name?.split(' ')[0] || 'Secretary' })}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t('dash.subtitle')}</p>
        </div>
        <Button onClick={() => navigate('/portal/claims')} data-testid="dashboard-view-claims-button">
          <FileCheck2 className="h-4 w-4 mr-2" />
          {t('dash.viewMyClaims')}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiles.map((tile) => {
          const Icon = tile.icon;
          return (
            <Card key={tile.key} data-testid={`tile-${tile.key}`}>
              <CardContent className="p-4">
                <div className={`inline-flex items-center justify-center h-9 w-9 rounded-lg mb-3 ${tile.tone}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="text-xs text-muted-foreground">{tile.label}</div>
                <div className="text-xl font-bold mt-0.5">{tile.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {stats?.claims_by_status && Object.keys(stats.claims_by_status).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-heading">{t('dash.section.claimStatus')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.claims_by_status).map(([s, n]) => (
                <Badge key={s} className={`${STATUS_TONE[s] || 'bg-muted'} text-xs`}>
                  {tStatus(s)}: {n}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {claims.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3 pb-3">
            <div>
              <CardTitle className="text-base font-heading">{t('dash.section.myClaims')}</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">{t('dash.section.myClaimsHint')}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/portal/claims')} data-testid="dashboard-all-claims-link">
              {t('common.viewAll')}
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="divide-y border-t">
              {claims.slice(0, 5).map((c) => (
                <li
                  key={c.id}
                  onClick={() => navigate(`/portal/claims?id=${c.id}`)}
                  className="py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-muted/40 px-2 rounded transition"
                  data-testid={`dashboard-claim-row-${c.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">
                      {(policies.find((p) => p.id === c.policy_id) || {}).title || t('claims.title')}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>{c.fiscal_year}</span>
                      <span>·</span>
                      <span className="tabular-nums">₹{(c.claim_amount || 0).toLocaleString('en-IN')}</span>
                      <span>·</span>
                      <span>{t('dash.docs', { n: (c.attachments || []).length })}</span>
                      {c.submitted_at && (
                        <>
                          <span>·</span>
                          <span>{t('dash.submittedOn', { date: new Date(c.submitted_at).toLocaleDateString(dateLocale) })}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <Badge className={`${STATUS_TONE[c.status] || 'bg-muted'} text-xs flex-shrink-0`}>
                    {tStatus(c.status)}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-heading">{t('dash.section.availablePolicies')}</CardTitle>
        </CardHeader>
        <CardContent>
          {policies.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">{t('dash.noPolicies')}</div>
          ) : (
            <div className="space-y-3">
              {policies.map((p) => (
                <div
                  key={p.id}
                  className="flex items-start justify-between gap-3 rounded-lg border bg-card p-4 hover:shadow-sm hover:border-emerald-400 transition cursor-pointer"
                  onClick={() => navigate(`/portal/policies/${p.id}`)}
                  data-testid={`policy-card-${p.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{p.title}</h3>
                      <Badge variant="outline" className="text-xs">{p.fiscal_year}</Badge>
                    </div>
                    {p.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{p.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      <span><strong className="text-foreground">₹{(p.eligible_amount || 0).toLocaleString('en-IN')}</strong> {t('dash.eligible')}</span>
                      <span>{t('dash.requiredDocsCount', { n: (p.required_documents || []).length })}</span>
                      {p.policy_document?.filename && (
                        <a
                          href={`${API}/grant-policies/${p.id}/document/download`}
                          target="_blank" rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="underline-offset-4 hover:underline text-foreground"
                        >
                          {p.policy_document.filename}
                        </a>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); navigate(`/portal/claims?policy=${p.id}`); }}
                    data-testid={`policy-start-claim-${p.id}`}
                  >
                    {t('dash.startClaim')}
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalDashboard;
